import React from 'react';
import { Box, styled } from '@mui/material';
import { ReactComponent as ArrowDownTrayIcon } from '../../assets/icons/arrow-down-tray.svg';
import { ReactComponent as PlayBadgeIcon } from '../../assets/icons/play.svg';
import { ReactComponent as PlayOverlayIcon } from '../../assets/icons/play-24.svg';

export const CardList = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    alignItems: 'stretch',
    gap: theme.spacing(4),
    width: '100%',
    '& > :only-child': {
        gridColumn: '1 / -1'
    }
}));

const CardShell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    minWidth: 0,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.semantic.bg.default,
    border: `1px solid ${theme.palette.semantic.int.selected}`,
    borderRadius: '12px',
    overflow: 'hidden'
}));

const ClickableCardShell = styled(CardShell)(({ theme }) => ({
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.primary[25]
    },
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.semantic.int.focused}`,
        outlineOffset: '2px'
    }
}));

const CardContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    flex: '1 1 0',
    minWidth: 0,
    boxSizing: 'border-box'
}));

const CardInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%'
}));

const BadgeRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%'
}));

const Badge = styled('span')(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    padding: '2px 8px',
    border: `1px solid ${theme.palette.primary[800]}`,
    borderRadius: '4px',
    fontFamily: '"TT Commons Pro"',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '17px',
    color: theme.palette.primary[800],
    whiteSpace: 'nowrap'
}));

const CardTitle = styled('h3')(({ theme }) => ({
    margin: 0,
    width: '100%',
    fontFamily: '"TT Commons Pro"',
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '26px',
    color: theme.palette.semantic.int.selected,
    textDecoration: 'underline',
    textUnderlinePosition: 'from-font'
}));

const CardMeta = styled('p')(({ theme }) => ({
    margin: 0,
    width: '100%',
    fontFamily: '"TT Commons Pro"',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '19px',
    color: theme.palette.semantic.text.default
}));

const CardDescription = styled('p')(({ theme }) => ({
    margin: 0,
    width: '100%',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '22px',
    color: theme.palette.semantic.text.default
}));

const MediaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    width: '100%'
}));

const Thumbnail = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '111px',
    height: '84px',
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: theme.palette.semantic.bg.inverse,
    color: theme.palette.semantic.icon.inverse
}));

const MediaText = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flex: '1 1 0',
    minWidth: 0
}));

export interface DocumentCardProps {
    typeLabel: string;
    formatLabel: string;
    title: string;
    date: string;
    description: string;
    onClick?: () => void;
}

export const DocumentCard = ({
    typeLabel,
    formatLabel,
    title,
    date,
    description,
    onClick
}: DocumentCardProps) => (
    <ClickableCardShell
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
            }
        }}
    >
        <CardContent>
            <CardInfo>
                <BadgeRow>
                    <Badge>{typeLabel}</Badge>
                    <Badge>
                        <ArrowDownTrayIcon aria-hidden="true" />
                        {formatLabel}
                    </Badge>
                </BadgeRow>
                <CardTitle>{title}</CardTitle>
                <CardMeta>{date}</CardMeta>
                <CardDescription>{description}</CardDescription>
            </CardInfo>
        </CardContent>
    </ClickableCardShell>
);

export interface VideoCardProps {
    typeLabel: string;
    formatLabel: string;
    title: string;
    duration: string;
    description: string;
}

export const VideoCard = ({
    typeLabel,
    formatLabel,
    title,
    duration,
    description
}: VideoCardProps) => (
    <CardShell sx={{ minHeight: '208px' }}>
        <CardContent>
            <CardInfo>
                <BadgeRow>
                    <Badge>{typeLabel}</Badge>
                    <Badge>
                        <PlayBadgeIcon aria-hidden="true" />
                        {formatLabel}
                    </Badge>
                </BadgeRow>
                <MediaRow>
                    <Thumbnail>
                        <PlayOverlayIcon aria-hidden="true" />
                    </Thumbnail>
                    <MediaText>
                        <CardTitle>{title}</CardTitle>
                        <CardMeta>{duration}</CardMeta>
                    </MediaText>
                </MediaRow>
                <CardDescription>{description}</CardDescription>
            </CardInfo>
        </CardContent>
    </CardShell>
);
